import React, { useState, useRef, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { BookDemoForm } from "@/components/BookDemoForm"
import { ChevronDownIcon, XMarkIcon, Bars3Icon } from "@heroicons/react/24/solid"
const logo = "/images/grandpad-enterprise-logo.svg";

interface DropdownItem {
  label: string
  href: string
  external?: boolean
}

interface NavDropdownProps {
  label: string
  items: DropdownItem[]
  isOpen: boolean
  isActive?: boolean
  currentPath?: string
  onToggle: () => void
  onClose: () => void
}

function NavDropdown({ label, items, isOpen, isActive, currentPath, onToggle, onClose }: NavDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, onClose])

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 text-body transition-colors hover:text-beacon pb-1 ${isActive ? 'text-beacon font-semibold border-b-2 border-beacon' : 'text-foreground border-b-2 border-transparent'}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {label}
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-background border border-muted rounded-lg shadow-lg z-50 py-2">
          {items.map((item) => (
            item.external ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2 text-body text-foreground hover:bg-muted hover:text-beacon transition-colors"
                onClick={onClose}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.label}
                to={item.href}
                className={`block px-4 py-2 text-body transition-colors hover:bg-muted hover:text-beacon ${currentPath === item.href ? 'text-beacon font-semibold border-l-3 border-beacon pl-3' : 'text-foreground'}`}
                onClick={(e) => {
                  if (currentPath === item.href) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'auto' });
                  }
                  onClose();
                }}
              >
                {item.label}
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  )
}

const aboutUsItems: DropdownItem[] = [
  { label: "Leadership", href: "https://www.grandpad.net/meet-the-team", external: true },
  { label: "Grand Advisors", href: "https://www.grandpad.net/meet-the-team", external: true },
  { label: "In the News", href: "https://www.grandpad.net/in-the-news", external: true },
]

const solutionsItems: DropdownItem[] = [
  { label: "Custom Solutions", href: "/solutions" },
  { label: "GrandPad in Action", href: "/grandpad-in-action" },
]

const provenResultsItems: DropdownItem[] = [
  { label: "Simple & Secure Engagement", href: "/simple-secure-engagement" },
  { label: "Turnkey Enablement", href: "/turnkey-enablement" },
  { label: "Evidence-Based Outcomes", href: "/evidence-based-outcomes" },
  { label: "Person-Centered Support", href: "/person-centered-support" },
]

const resourcesItems: DropdownItem[] = [
  { label: "Case Studies", href: "/case-studies" },
]

export function Navigation() {
  const location = useLocation()
  const [showDemoForm, setShowDemoForm] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null)

  const isProvenActive = provenResultsItems.some(item => location.pathname === item.href)
  const isSolutionsActive = solutionsItems.some(item => location.pathname === item.href)
  const isResourcesActive = resourcesItems.some(item => location.pathname === item.href)

  const handleDropdownToggle = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name)
  }

  const handleDropdownClose = () => {
    setOpenDropdown(null)
  }

  const toggleMobileDropdown = (name: string) => {
    setMobileDropdown(mobileDropdown === name ? null : name)
  }

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-beacon focus:px-6 focus:py-3 focus:text-body focus:text-sailcloth focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="w-full bg-sailcloth sticky top-0 z-50 shadow-[0_4px_20px_0_rgba(0,0,0,0.08)] px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32">
        <nav className="w-full max-w-[1440px] mx-auto flex items-center justify-between py-4">
          <a href="/" aria-label="GrandPad Enterprise Edition Home">
            <img 
              src={logo} 
              alt="GrandPad Enterprise Edition" 
              className="h-12 w-auto min-w-[180px] flex-shrink-0"
              width={384}
              height={97}
              fetchPriority="high"
              decoding="async"
            />
          </a>
          
          {/* Desktop navigation */}
          <div className="flex items-center gap-8 max-lg:hidden">
            <NavDropdown
              label="Solutions"
              items={solutionsItems}
              isOpen={openDropdown === 'solutions'}
              isActive={isSolutionsActive}
              currentPath={location.pathname}
              onToggle={() => handleDropdownToggle('solutions')}
              onClose={handleDropdownClose}
            />
            <NavDropdown
              label="Proven Results"
              items={provenResultsItems}
              isOpen={openDropdown === 'proven'}
              isActive={isProvenActive}
              currentPath={location.pathname}
              onToggle={() => handleDropdownToggle('proven')}
              onClose={handleDropdownClose}
            />
            
            <NavDropdown
              label="About Us"
              items={aboutUsItems}
              isOpen={openDropdown === 'about'}
              currentPath={location.pathname}
              onToggle={() => handleDropdownToggle('about')}
              onClose={handleDropdownClose}
            />
            
            <NavDropdown
              label="Resources"
              items={resourcesItems}
              isOpen={openDropdown === 'resources'}
              isActive={isResourcesActive}
              currentPath={location.pathname}
              onToggle={() => handleDropdownToggle('resources')}
              onClose={handleDropdownClose}
            />
            
            <a 
              href="https://grandpad.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-body text-foreground hover:text-beacon transition-colors"
            >
              Consumer
            </a>
            
            <Button 
              variant="primary" 
              size="default"
              onClick={() => setShowDemoForm(true)}
              className="rounded-full px-6"
            >
              Book a Demo
            </Button>
          </div>

          {/* Mobile menu button */}
          <button 
            className="hidden max-lg:flex items-center justify-center min-h-11 min-w-11 -mr-2 text-foreground"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="hidden max-lg:block bg-background border-t border-muted">
            <div className="px-8 py-4 space-y-4 max-sm:px-4">
              {/* Solutions dropdown */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('solutions')}
                  className="flex items-center justify-between w-full text-body text-foreground py-2"
                >
                  Solutions
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${mobileDropdown === 'solutions' ? 'rotate-180' : ''}`} />
                </button>
                {mobileDropdown === 'solutions' && (
                  <div className="pl-4 space-y-2 mt-2">
                    {solutionsItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block text-body text-foreground hover:text-beacon py-1"
                        onClick={(e) => {
                          if (location.pathname === item.href) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'auto' });
                          }
                          setMobileMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* Proven Results dropdown */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('proven')}
                  className="flex items-center justify-between w-full text-body text-foreground py-2"
                >
                  Proven Results
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${mobileDropdown === 'proven' ? 'rotate-180' : ''}`} />
                </button>
                {mobileDropdown === 'proven' && (
                  <div className="pl-4 space-y-2 mt-2">
                    {provenResultsItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block text-body text-foreground hover:text-beacon py-1"
                        onClick={(e) => {
                          if (location.pathname === item.href) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'auto' });
                          }
                          setMobileMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* About Us dropdown */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('about')}
                  className="flex items-center justify-between w-full text-body text-foreground py-2"
                >
                  About Us
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${mobileDropdown === 'about' ? 'rotate-180' : ''}`} />
                </button>
                {mobileDropdown === 'about' && (
                  <div className="pl-4 space-y-2 mt-2">
                    {aboutUsItems.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-body text-foreground hover:text-beacon py-1"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Resources dropdown */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('resources')}
                  className="flex items-center justify-between w-full text-body text-foreground py-2"
                >
                  Resources
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${mobileDropdown === 'resources' ? 'rotate-180' : ''}`} />
                </button>
                {mobileDropdown === 'resources' && (
                  <div className="pl-4 space-y-2 mt-2">
                    {resourcesItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block text-body text-foreground hover:text-beacon py-1"
                        onClick={(e) => {
                          if (location.pathname === item.href) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'auto' });
                          }
                          setMobileMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Consumer link */}
              <a 
                href="https://grandpad.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-body text-foreground hover:text-beacon py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Consumer
              </a>
              
              {/* Book a Demo button */}
              <Button 
                variant="primary" 
                size="default"
                onClick={() => {
                  setShowDemoForm(true)
                  setMobileMenuOpen(false)
                }}
                className="rounded-full px-6 w-full"
              >
                Book a Demo
              </Button>
            </div>
          </div>
        )}
      </header>

      <BookDemoForm 
        open={showDemoForm} 
        onOpenChange={setShowDemoForm}
      />
    </>
  )
}